import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Copy,
  FileCode2,
  KeyRound,
  RefreshCcw,
  Webhook,
} from "lucide-react";
import { toast } from "react-toastify";
import axiosClient from "../../api/axiosClient";
import PageHeader from "../../components/common/PageHeader";
import ApiKeysSection from "./ApiKeysSection";
import WebhooksSection from "./WebhooksSection";
import WebhookLogsTable from "./WebhookLogsTable";

function normalizeApiBase(url) {
  const clean = String(url || "").trim().replace(/\/+$/, "");
  if (!clean) return "http://localhost:7113/api";
  return clean.endsWith("/api") ? clean : `${clean}/api`;
}

const POLL_INTERVAL_MS = 25000;
const REFRESH_SECTIONS = ["endpoints", "logs"];
const TAB_ITEMS = [
  {
    id: "get-started",
    label: "Setup Guide",
    description: "Follow the three-step setup path for keys, endpoints, and test delivery.",
  },
  {
    id: "api-access",
    label: "API Keys",
    description: "Create and revoke workspace API keys for Public API requests.",
  },
  {
    id: "webhooks",
    label: "Webhook Endpoints",
    description: "Configure endpoint URLs, subscriptions, secrets, and test callbacks.",
  },
  {
    id: "monitoring",
    label: "Delivery Logs",
    description: "Review delivery activity, filter logs, and replay failed webhook deliveries.",
  },
  {
    id: "advanced",
    label: "API Reference",
    description: "Reference the base URL, example requests, payload shape, and current caveats.",
  },
];

const toData = response =>
  response?.data?.data ?? response?.data?.payload ?? response?.data ?? null;

const getErrorMessage = (error, fallback) =>
  error?.response?.data?.message || fallback;

const parseEvents = value => {
  if (Array.isArray(value)) return value;
  if (typeof value !== "string" || !value.trim()) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const normalizeEndpoint = row => ({
  id: row?.id,
  url: row?.url || "",
  events: parseEvents(row?.events ?? row?.subscribedEventsJson),
  isActive: row?.isActive !== false,
  createdAtUtc: row?.createdAtUtc,
  updatedAtUtc: row?.updatedAtUtc,
  healthStatus: row?.healthStatus || "NoData",
  lastSuccessAtUtc: row?.lastSuccessAtUtc || null,
  lastFailureAtUtc: row?.lastFailureAtUtc || null,
  pendingRetryCount: Number(row?.pendingRetryCount || 0),
  recentFailureCount: Number(row?.recentFailureCount || 0),
  lastFailureStatusCode:
    row?.lastFailureStatusCode === null || row?.lastFailureStatusCode === undefined
      ? null
      : row.lastFailureStatusCode,
  lastFailureSnippet: row?.lastFailureSnippet || "",
});

function formatRefreshTimestamp(value) {
  if (!value) return "Loading initial data...";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Loading initial data...";
  return date.toLocaleTimeString([], {
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
  });
}

function needsAttention(status) {
  return status === "Failing" || status === "Degraded";
}

export default function IntegrationsPage() {
  const [activeTab, setActiveTab] = useState("get-started");
  const [endpoints, setEndpoints] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const refreshTickRef = useRef(0);
  const refreshCompletionsRef = useRef(new Set());
  const refreshActiveRef = useRef(false);

  const apiBaseUrl = useMemo(() => {
    const raw = process.env.REACT_APP_API_BASE_URL || "http://localhost:7113";
    return normalizeApiBase(raw);
  }, []);

  const attentionCount = useMemo(
    () => endpoints.filter(endpoint => needsAttention(endpoint?.healthStatus)).length,
    [endpoints]
  );
  const activeTabMeta = useMemo(
    () => TAB_ITEMS.find(tab => tab.id === activeTab) || TAB_ITEMS[0],
    [activeTab]
  );

  const triggerRefresh = useCallback(() => {
    refreshActiveRef.current = true;
    refreshCompletionsRef.current = new Set();
    setRefreshing(true);
    setLastRefreshedAt(new Date());
    setRefreshTick(value => {
      const next = value + 1;
      refreshTickRef.current = next;
      return next;
    });
  }, []);

  const handleSectionRefreshed = useCallback((section, tick) => {
    if (!refreshActiveRef.current || tick !== refreshTickRef.current) {
      return;
    }

    refreshCompletionsRef.current.add(section);
    if (REFRESH_SECTIONS.every(item => refreshCompletionsRef.current.has(item))) {
      refreshActiveRef.current = false;
      setRefreshing(false);
    }
  }, []);

  const loadEndpoints = useCallback(async (tick = refreshTick) => {
    try {
      const res = await axiosClient.get("/webhooks/endpoints", {
        __silentGenericToast: true,
      });
      const data = toData(res);
      const list = Array.isArray(data)
        ? data
        : data?.items || data?.endpoints || [];
      setEndpoints((Array.isArray(list) ? list : []).map(normalizeEndpoint));
    } catch (error) {
      if (error?.response?.status !== 429) {
        toast.error(getErrorMessage(error, "Failed to load webhook endpoints"), {
          toastId: "integrations-endpoints-load",
        });
      }
    } finally {
      handleSectionRefreshed("endpoints", tick);
    }
  }, [refreshTick, handleSectionRefreshed]);

  const handleWebhooksRefreshed = useCallback(
    tick => {
      handleSectionRefreshed("endpoints", tick);
    },
    [handleSectionRefreshed]
  );

  const handleLogsRefreshed = useCallback(
    tick => {
      handleSectionRefreshed("logs", tick);
    },
    [handleSectionRefreshed]
  );

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      triggerRefresh();
    }, POLL_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [triggerRefresh]);

  useEffect(() => {
    loadEndpoints(refreshTick);
  }, [loadEndpoints, refreshTick]);

  const sendCurlSnippet = useMemo(
    () =>
      `curl --request POST "${apiBaseUrl}/public/v1/1234567890/messages" \\
  --header "Authorization: Bearer xb_live_xxxxxxxxxxxxxxxxxxxxxxxx" \\
  --header "Idempotency-Key: example-unique-key" \\
  --header "Content-Type: application/json" \\
  --data '{
    "messaging_product": "whatsapp",
    "to": "919876543210",
    "type": "text",
    "text": {
      "body": "Hello from Public API"
    }
  }'`,
    [apiBaseUrl]
  );

  const callbackSnippet = useMemo(
    () =>
      `{
  "id": "evt_124",
  "type": "message.delivered",
  "createdAt": "2026-03-26T12:35:30Z",
  "data": {
    "businessId": "aaaaaaaa-bbbb-cccc-dddd-eeeeeeeeeeee",
    "logId": "11111111-2222-3333-4444-555555555555",
    "messageId": "wamid.HBgM...",
    "providerMessageId": "wamid.HBgM...",
    "recipientNumber": "919876543210",
    "status": "delivered",
    "eventTimestampUtc": "2026-03-26T12:35:28Z",
    "errorMessage": null,
    "source": "public-api",
    "origin": "meta-status-webhook",
    "failureStage": null
  }
}`,
    []
  );

  const eventSummaries = useMemo(
    () => [
      {
        type: "message.sent",
        description: "Queued after a successful send is persisted.",
      },
      {
        type: "message.delivered",
        description: "Queued when the provider reports delivery.",
      },
      {
        type: "message.read",
        description: "Queued when the provider reports read status.",
      },
      {
        type: "message.failed",
        description: "Used for send-time and later provider failures.",
      },
      {
        type: "message.inbound.received",
        description: "Queued when a new inbound message is inserted.",
      },
    ],
    []
  );

  const copyText = async value => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="p-6 bg-[#f5f6f7] min-h-[calc(100vh-80px)] space-y-6">
      <div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <PageHeader
            icon={FileCode2}
            title="Integrations"
            subtitle="Manage API keys, webhooks, and developer endpoints for your workspace."
          />
          <div className="flex flex-col items-start gap-2 sm:items-end">
            <button
              type="button"
              onClick={triggerRefresh}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
            >
              <RefreshCcw size={14} className={refreshing ? "animate-spin" : ""} />
              Refresh
            </button>
            <p className="text-sm text-slate-600">
              Last refreshed: {formatRefreshTimestamp(lastRefreshedAt)}
            </p>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 text-sm text-slate-600">
          {refreshing ? <p className="text-xs text-slate-500">Refreshing...</p> : null}
          {attentionCount > 0 ? (
            <p className="inline-flex items-center gap-1 text-sm font-medium text-rose-700">
              <AlertTriangle size={14} />
              {attentionCount} endpoint{attentionCount === 1 ? "" : "s"}{" "}
              {attentionCount === 1 ? "needs" : "need"} attention
            </p>
          ) : null}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[240px_minmax(0,1fr)] xl:items-start">
        <aside className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-6">
          <nav className="space-y-1">
            {TAB_ITEMS.map(tab => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                aria-label={`${tab.label}: ${tab.description}`}
                className={`w-full rounded-lg border px-3 py-3 text-left transition ${
                  activeTab === tab.id
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900 shadow-sm"
                    : "border-transparent text-slate-500 hover:border-slate-200 hover:bg-slate-50 hover:text-slate-900"
                }`}
              >
                <div className="group relative flex items-center justify-between gap-3">
                  <span
                    className={`text-sm ${
                      activeTab === tab.id ? "font-semibold" : "font-medium"
                    }`}
                  >
                    {tab.label}
                  </span>
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      activeTab === tab.id ? "bg-emerald-600" : "bg-slate-200"
                    }`}
                  />
                  <span className="pointer-events-none absolute left-[calc(100%+14px)] top-1/2 z-20 hidden w-56 -translate-y-1/2 rounded-lg border border-slate-200 bg-slate-900 px-3 py-2 text-xs font-medium leading-5 text-white opacity-0 shadow-xl transition duration-150 group-hover:opacity-100 xl:block">
                    {tab.description}
                  </span>
                </div>
              </button>
            ))}
          </nav>
        </aside>

        <div className="min-w-0 space-y-5">
          {activeTab !== "get-started" && activeTab !== "api-access" ? (
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                {activeTabMeta.label}
              </p>
              <h2 className="mt-2 text-xl font-semibold text-slate-900">
                {activeTabMeta.label}
              </h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
                {activeTabMeta.description}
              </p>
            </div>
          ) : null}

          {activeTab === "get-started" ? (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="max-w-2xl">
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-emerald-700">
                      Guided setup
                    </p>
                    <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                      Get your integration ready in three steps
                    </h3>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                      Start with an API key, register a webhook endpoint, then
                      send a signed test callback from the Webhooks tab to
                      verify reachability and signature handling before going
                      live.
                    </p>
                  </div>
                  <a
                    href="/integrations"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    View public contract
                  </a>
                </div>
              </div>

              <div className="grid gap-4 xl:grid-cols-3">
                <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 text-slate-900">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700">
                      1
                    </span>
                    <div className="flex items-center gap-2">
                      <KeyRound size={16} className="text-emerald-600" />
                      <p className="text-sm font-semibold">Create API Key</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Generate a bearer key for your workspace. Raw keys are
                    shown once, so store them before you start sending requests.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab("api-access")}
                    className="mt-auto inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
                  >
                    Open API Access
                  </button>
                </div>

                <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 text-slate-900">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700">
                      2
                    </span>
                    <div className="flex items-center gap-2">
                      <Webhook size={16} className="text-emerald-600" />
                      <p className="text-sm font-semibold">Add Webhook</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Register an endpoint URL, keep it active, and subscribe it
                    to the callback events your integration needs to consume.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab("webhooks")}
                    className="mt-auto inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
                  >
                    Open Webhooks
                  </button>
                </div>

                <div className="flex h-full flex-col rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="flex items-center gap-3 text-slate-900">
                    <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-emerald-50 text-xs font-semibold text-emerald-700">
                      3
                    </span>
                    <div className="flex items-center gap-2">
                      <CheckCircle2 size={16} className="text-emerald-600" />
                      <p className="text-sm font-semibold">Send Test Callback</p>
                    </div>
                  </div>
                  <p className="mt-3 text-sm leading-6 text-slate-600">
                    Use the row-level test callback action in the Webhooks tab
                    to send a signed <code>webhook.test</code> event to one
                    active endpoint and confirm it is reachable.
                  </p>
                  <button
                    type="button"
                    onClick={() => setActiveTab("webhooks")}
                    className="mt-auto inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-800 hover:bg-emerald-100"
                  >
                    Go to Webhooks
                  </button>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4 shadow-sm">
                <p className="text-sm font-medium text-slate-900">
                  Supported callback events
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  Active webhook endpoints can subscribe to{" "}
                  {eventSummaries.map((event, index) => (
                    <span key={event.type}>
                      <code>{event.type}</code>
                      {index < eventSummaries.length - 1 ? ", " : "."}
                    </span>
                  ))}
                </p>
              </div>
            </div>
          ) : null}

          {activeTab === "api-access" ? (
            <div className="space-y-5">
              <ApiKeysSection />

              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
                  <div className="max-w-2xl">
                    <p className="text-sm font-semibold text-slate-900">
                      Use this key in your first request
                    </p>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      After you create an API key, use it as a bearer token
                      with the base URL below. Your first public send request
                      will typically go to{" "}
                      <code>{apiBaseUrl}/public/v1/{"{phone_number_id}"}/messages</code>.
                    </p>
                  </div>
                  <a
                    href="/integrations"
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
                  >
                    View public contract
                  </a>
                </div>

                <div className="mt-5 grid gap-4 xl:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                    <p className="text-sm font-medium text-slate-900">API Base URL</p>
                    <p className="mt-2 text-sm leading-6 text-slate-600">
                      Combine this base URL with the public route when sending
                      requests from your server or backend job.
                    </p>
                    <div className="mt-3 flex items-center gap-2">
                      <code className="flex-1 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                        {apiBaseUrl}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyText(apiBaseUrl)}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
                      >
                        <Copy size={16} /> Copy
                      </button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 p-4">
                    <p className="text-sm font-medium text-slate-900">Request essentials</p>
                    <div className="mt-3 space-y-3 text-sm text-slate-600">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Authorization
                        </p>
                        <code className="mt-1 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                          Authorization: Bearer xb_live_...
                        </code>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          Idempotency (optional, recommended)
                        </p>
                        <code className="mt-1 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700">
                          Idempotency-Key: example-unique-key
                        </code>
                        <p className="mt-1 text-xs text-slate-500">
                          Adds duplicate protection for retries. Use a new key for
                          each new message.
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
                          First route
                        </p>
                        <code className="mt-1 block rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 break-all">
                          POST {apiBaseUrl}/public/v1/{"{phone_number_id}"}/messages
                        </code>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
                <p className="text-sm font-semibold text-slate-900">
                  Public send example
                </p>
                <p className="mt-2 text-sm leading-6 text-slate-600">
                  After generating a key, replace the sample bearer token and
                  host values below with your own workspace credentials and use
                  this request as your first send example.
                </p>
                <div className="relative mt-4">
                  <pre className="overflow-x-auto rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
                    {sendCurlSnippet}
                  </pre>
                  <button
                    type="button"
                    onClick={() => copyText(sendCurlSnippet)}
                    className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-slate-100 hover:bg-slate-700"
                  >
                    <Copy size={14} /> Copy
                  </button>
                </div>
              </div>
            </div>
          ) : null}

          {activeTab === "webhooks" ? (
            <WebhooksSection
              onEndpointsChange={setEndpoints}
              refreshTick={refreshTick}
              onRefreshComplete={handleWebhooksRefreshed}
            />
          ) : null}

          {activeTab === "monitoring" ? (
            <WebhookLogsTable
              endpoints={endpoints}
              refreshTick={refreshTick}
              onRefreshComplete={handleLogsRefreshed}
              onHealthChanged={triggerRefresh}
            />
          ) : null}

          {activeTab === "advanced" ? (
            <div className="space-y-5">
              <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
                <div className="mb-3">
                  <p className="text-sm font-medium text-slate-700 mb-2">
                    API Base URL
                  </p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm text-slate-700">
                      {apiBaseUrl}
                    </code>
                    <button
                      type="button"
                      onClick={() => copyText(apiBaseUrl)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50"
                    >
                      <Copy size={16} /> Copy
                    </button>
                  </div>
                </div>

                <div>
                  <div className="grid gap-4 xl:grid-cols-2">
                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">
                        Public send example
                      </p>
                      <div className="relative">
                        <pre className="bg-slate-900 text-slate-100 text-xs rounded-md p-3 overflow-x-auto">
                          {sendCurlSnippet}
                        </pre>
                        <button
                          type="button"
                          onClick={() => copyText(sendCurlSnippet)}
                          className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-slate-100 hover:bg-slate-700"
                        >
                          <Copy size={14} /> Copy
                        </button>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm font-medium text-slate-700 mb-2">
                        Callback payload example
                      </p>
                      <div className="relative">
                        <pre className="bg-slate-900 text-slate-100 text-xs rounded-md p-3 overflow-x-auto">
                          {callbackSnippet}
                        </pre>
                        <button
                          type="button"
                          onClick={() => copyText(callbackSnippet)}
                          className="absolute top-2 right-2 inline-flex items-center gap-1 rounded-md border border-slate-600 bg-slate-800 px-2 py-1 text-xs text-slate-100 hover:bg-slate-700"
                        >
                          <Copy size={14} /> Copy
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-amber-200 p-5 shadow-sm">
                <div className="flex items-start gap-3">
                  <CheckCircle2 size={18} className="mt-0.5 text-amber-700" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">
                      Current limitations to keep in mind
                    </p>
                    <ul className="mt-2 space-y-2 text-sm leading-6 text-slate-600">
                      <li>
                        Public send supports only <code>text</code> and{" "}
                        <code>template</code>.
                      </li>
                      <li>
                        Template support is intentionally narrow and validated
                        strictly.
                      </li>
                      <li>
                        Provider-returned send failure can still be HTTP{" "}
                        <code>200</code> with <code>success=false</code>.
                      </li>
                      <li>
                        Inbound reply correlation is currently provider-message
                        based.
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
